import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { KeyNodeType } from '@overtur-monorepo/keying-shared'
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { take } from 'rxjs/operators'
import { fadeInOnly } from '../../../animations/fadeInOnly'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'
import { KeySystem, Structure } from '../../../models/dev.test.models'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { KeyingCylinderService } from '../../../services/keying.cylinder.service'
import { KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { LoadingService } from '../../../services/loading.service'
import { AddCompetitorPinningDialog } from '../../../dialogs/add-competitor-pinning-dialog/add-competitor-pinning-dialog.component'
import { ChangeLogConfirmationDialogComponent } from '../../../components/matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'

@Component({
    selector: 'app-operated-by-table',
    templateUrl: './operated-by-table.component.html',
    styleUrls: ['./operated-by-table.component.scss'],
    animations: [fadeInOnly],
    standalone: false
})
export class OperatedByTableComponent implements OnInit, OnChanges {
  keyNodeType = KeyNodeType

  @Input() cylindersDisplayColumns
  @Input() filteredCylinders
  @Input() selectedCylinder
  @Input() editing = false
  @Input() structure: Structure
  @Input() keySystem: KeySystem
  @Input() selectedMasterKey: MasterKey

  @Output() onEdit = new EventEmitter<any>()
  @Output() selectNewCylinder = new EventEmitter<any>()

  keysForm: FormGroup
  operatorsForm: FormGroup

  expanded = true

  focus = {
    keyName: false,
    prefix: false,
    from: false,
    to: false,
    suffix: false,
  }

  public errorMessages = {
    keyName: [{ type: 'required', message: 'A Name is required.' }],
    prefix: [{ type: 'required', message: 'A Prefix is required.' }],
    from: [{ type: 'required', message: 'A From value is required.' }],
    to: [{ type: 'required', message: 'A To value is required.' }],
    suffix: [{ type: 'required', message: 'A Suffix is required.' }],
  }

  constructor(
    public form: FormBuilder,
    private keyingCylinderService: KeyingCylinderService,
    private keyingDataSetsService: KeyingDataSetsService,
    private errorService: ErrorService,
    private dialogService: DialogService,
    private loadingService: LoadingService
  ) {
    this.keysForm = this.form.group({
      keys: new FormControl(null),
    })
    this.operatorsForm = this.form.group({
      prefix: new FormControl(),
      from: new FormControl(),
      to: new FormControl(),
      suffix: new FormControl(),
    })
  }

  /* istanbul ignore next */
  ngOnInit(): void {}

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges) {
    if (changes?.selectedCylinder?.currentValue) {
      this.keysForm.reset()
      this.operatorsForm.reset()
    }
  }

  /* istanbul ignore next */
  addOperators() {
    const keysControls = this.keysForm.controls
    const convertToLowerCase = keysControls.keys.value.toLowerCase()
    if (
      convertToLowerCase.includes('conl') &&
      (this.structure.keywayFamilyId === 9 || this.structure.keywayFamilyId === 10)
    ) {
      {
        const dialogRef = this.dialogService.open(AddCompetitorPinningDialog)
        dialogRef.afterClosed().subscribe((data) => {
          if (data) {
            this.updateCylinderWithLFICPinning(data)
          }
        })
      }
    } else {
      this.updateCylinderWithLFICPinning()
    }
  }

  updateCylinderWithLFICPinning(cycPinningInfo = '') {
    const keysControls = this.keysForm.controls
    const data = {
      keySetSymbols: keysControls.keys.value,
      competitorId: cycPinningInfo,
    }
    this.loadingService.set({ isLoading: true, message: 'Adding Operators' })
    this.keyingCylinderService
      .addOperators(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        data
      )
      .pipe(take(1))
      .subscribe(
        (newCylinder) => {
          this.keysForm.reset()
          this.operatorsForm.reset()
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)

          this.selectNewCylinder.emit(newCylinder)

          /* Get Structure Details Everytime Added New Cylinder */
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)

          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  addXKeyOperators() {
    const keysControls = this.keysForm.controls
    if (keysControls.keys.value) {
      const convertToLowerCase = keysControls.keys.value.toLowerCase()
      if (
        convertToLowerCase.includes('conl') &&
        (this.structure.keywayFamilyId === 9 || this.structure.keywayFamilyId === 10)
      ) {
        {
          const dialogRef = this.dialogService.open(AddCompetitorPinningDialog)
          dialogRef.afterClosed().subscribe((data) => {
            if (data) {
              this.updateXkeyWithLFICPinning(data)
            }
          })
        }
      } else {
        this.updateXkeyWithLFICPinning()
      }
    } else {
      this.updateXkeyWithLFICPinning()
    }
  }

  updateXkeyWithLFICPinning(xkeyPinningInfo = '') {
    const keysControls = this.keysForm.controls
    const operatorsControls = this.operatorsForm.controls

    const data = {
      keySetSymbols: keysControls.keys.value,
      competitorId: xkeyPinningInfo,
      prefix: operatorsControls.prefix.value,
      from: operatorsControls.from.value,
      to: operatorsControls.to.value,
      suffix: operatorsControls.suffix.value,
    }

    this.loadingService.set({ isLoading: true, message: 'Adding CrossKey Operators' })
    this.keyingCylinderService
      .addXKeyOperators(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        data
      )
      .pipe(take(1))
      .subscribe(
        () => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.keysForm.reset()
          this.operatorsForm.reset()
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)
          /* Get Structure Details Everytime Added CrossKey Operator */
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  addCylinder() {
    const keysControls = this.keysForm.controls

    const data = {
      keySetSymbols: keysControls.keys.value,
    }
    this.loadingService.set({ isLoading: true, message: 'Adding Cylinders' })
    this.keyingCylinderService
      .addCylinder(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        data
      )
      .pipe(take(1))
      .subscribe(
        (newCylinder) => {
          this.keysForm.reset()
          this.operatorsForm.reset()
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)

          this.selectNewCylinder.emit(newCylinder)

          /* Get Structure Details Everytime Added New Cylinder */
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)

          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  delete() {
    const auditTrailRequest = {
      reasonCode: null,
      description: null,
    }
    if (this.selectedCylinder.isPublished || this.selectedCylinder.isLegacyPublished) {
      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        showCancel: true,
        actionType: 'Cylinder Updated',
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: this.selectedCylinder.name,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.deleteOperatorForCyclinder(result.auditTrailRequest)
        }
      })
    } else {
      this.deleteOperatorForCyclinder(auditTrailRequest)
    }
  }

  /* istanbul ignore next */
  deleteOperatorForCyclinder(auditTrailRequest) {
    const keysControls = this.keysForm.controls
    const operatorsControls = this.operatorsForm.controls

    const data = {
      keySetSymbols: keysControls.keys.value,
      prefix: operatorsControls.prefix.value,
      from: operatorsControls.from.value,
      to: operatorsControls.to.value,
      suffix: operatorsControls.suffix.value,
      auditTrailRequest: auditTrailRequest,
    }
    this.loadingService.set({ isLoading: true, message: 'Removing Operators' })
    this.keyingCylinderService
      .deleteOperators(this.structure.id, this.keySystem.id, this.selectedCylinder.id, data)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.keysForm.reset()
          this.operatorsForm.reset()
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          if (error.error.message.includes('a duplicate cylinder would be created')) {
            let data = {
              title: 'Delete Cyclinder',
              message: error.error.message,
              cancel: 'Cancel',
              confirm: 'Ok',
              showCancel: true,
              reasonRequired: false,
            }

            let dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

            dialogRef.afterClosed().subscribe((result) => {
              if (result.confirmed) {
                this.keyingCylinderService.onDeleteCylinderSelected.next(true)
              }
            })
          } else {
            this.errorService.throw(error)
          }
        }
      )
  }

  /* istanbul ignore next */
  deleteConfirm() {
    let data = {
      title: 'Delete Operator(s)',
      message: `Are you sure you want to remove these operator(s)?`,
      cancel: 'Cancel',
      confirm: 'Delete',
      showCancel: true,
      reasonRequired: false,
    }

    if (this.keySystem.isPublished) {
      data.message = 'This cylinder has been published. Are you sure you want to delete?'
      data.reasonRequired = true
    }

    let dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.delete()
      }
    })
  }

  /* istanbul ignore next */
  toggleEdit() {
    this.keysForm.reset()
    this.operatorsForm.reset()
    this.editing = !this.editing
    this.onEdit.emit()
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  isCrossKeyedCylinder(): boolean {
    return (
      this.selectedCylinder &&
      (this.selectedCylinder.cylinderTypeId === 2 || this.selectedCylinder.cylinderTypeId === 4)
    )
  }
}
